import safeToBn from '@blockcatio/tabby-library/lib/utilities/safeToBn';
import { ZERO_ADDRESS } from '@blockcatio/tabby-library/lib/constants';

import TabbyPayContractv1 from './TabbyPayContractv1';
import { ActionStatusEnum } from '../../constants';

/**
 * The TabbyPay contract
 */
class TabbyPayContractv2 extends TabbyPayContractv1 {
  /**
   * Creates the transaction for sending a payment
   *
   * @param {Object} paymentDetails
   * @param {BN} gasForward
   * @param {BN} ethPaymentFee
   * @param {Boolean} payWithCat
   * @param {Number} source
   * @param {String} gasPrice
   * @param {Number} gasLimit?
   * @param {Node|String} formattedTokenAmount?
   * @return {Object}
   */
  sendPaymentTransaction(
    paymentDetails,
    gasForward,
    ethPaymentFee,
    payWithCat,
    source,
    gasPrice,
    gasLimit = 0,
    formattedTokenAmount = null,
  ) {
    // If sending ETH, set value to the amount.
    const value =
      paymentDetails.tokenAddress === ZERO_ADDRESS ? safeToBn(paymentDetails.amount) : safeToBn(0);
    // If paying with ETH, set the fee value to the eth fee.
    const feeValue = payWithCat ? safeToBn(0) : ethPaymentFee;

    const transaction = this.contract.methods.createPayment(
      paymentDetails.destination,
      paymentDetails.tokenAddress,
      paymentDetails.amount.toString(),
      gasForward,
      source,
      payWithCat,
    );

    let gasLimitIncrease = 0;
    // Add more to the gas limit to cover the gas forward txn.
    if (!gasForward.eq(safeToBn(0))) {
      gasLimitIncrease = this.config.gasForwardGasLimitIncrease;
    }

    return {
      transaction,
      contract: this.config.address,
      sendOptions: {
        to: paymentDetails.destination,
        from: paymentDetails.sender,
        value: value
          .add(feeValue)
          .add(gasForward)
          .toString(),
        gasPrice,
        gas: gasLimit + gasLimitIncrease,
      },
      formattedTokenAmount,
    };
  }

  /**
   * Fetches all payments for a given address
   *
   * @param {String} address
   * @param {Number} latestBlockNumber
   * @return {Promise<Object[]>} - the payments
   */
  fetchPayments(address, latestBlockNumber) {
    return super.fetchPaymentEvents(address, latestBlockNumber, this.eventToPayment);
  }

  /**
   * Fetch the payment fee if paying with ETH.
   *
   * @returns {Promise<BN>}
   */
  fetchEthPaymentFee() {
    return this.contract.methods
      .ethFee()
      .call()
      .then(fee => safeToBn(fee));
  }

  /**
   * Fetch the payment fee if paying with CAT.
   *
   * @returns {Promise<BN>}
   */
  fetchCatPaymentFee() {
    return this.contract.methods
      .catFee()
      .call()
      .then(fee => safeToBn(fee));
  }

  /**
   * Generate a payment object from a CreatePayment event.
   *
   * @param {Object} event
   * @param {String|address} address
   * @param {Object} blocksByNum
   *
   * @return {Object}
   */
  eventToPayment(event, address, blocksByNum) {
    return {
      id: event.returnValues.id,
      destination: event.returnValues.destination.toLowerCase(),
      sender: event.returnValues.sender.toLowerCase(),
      amount: event.returnValues.amount,
      tokenAddress: event.returnValues.token,
      sentTimestamp: blocksByNum[event.blockNumber].timestamp,
      blockNumber: event.blockNumber,
      status: ActionStatusEnum.pending,
      transactionHash: event.transactionHash,
      contract: this.id,
      type: event.returnValues.destination.toLowerCase() === address ? 'incoming' : 'outgoing',
    };
  }
}

export default TabbyPayContractv2;



// WEBPACK FOOTER //
// ./src/services/TabbyPayContracts/TabbyPayContractv2.js